import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import { ApiError, getAccountActivationRequest, type AccountActivation } from '@/auth/api';
import { passwordConfirmationError } from '@/auth/registration';
import { useAuth } from '@/auth/useAuth';
import { hasQuotationDraft } from '@/features/operations/quotationDraft';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

type PageState = 'loading' | 'ready' | 'invalid' | 'unavailable' | 'activated';
type FieldErrors = { password?: string; passwordConfirmation?: string; phone?: string };

/// The same rules the registration applies, so a password is refused here before the backend.
function validatePassword(password: string): string | undefined {
  if (Array.from(password).length < 10) return 'La contraseña debe tener al menos 10 caracteres.';
  if (!/\p{Lu}/u.test(password)) return 'Agregá al menos una mayúscula.';
  if (!/\p{N}/u.test(password)) return 'Agregá al menos un número.';
  if (!/[^\p{L}\p{N}\s]/u.test(password)) return 'Agregá al menos un carácter especial.';
  if (new TextEncoder().encode(password).length > 72) return 'La contraseña es demasiado larga.';
  return undefined;
}

/**
 * Activates an account PhoneTec created at the counter, or that somebody claimed by
 * registering with its email (ADR 0008). Following the invitation proves the email; this page
 * is where the password is chosen, and it signs the owner in.
 */
export function CustomerActivateAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activateAccount } = useAuth();
  const token = searchParams.get('token');
  const [state, setState] = useState<PageState>('loading');
  const [activation, setActivation] = useState<AccountActivation | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const started = useRef(false);

  const load = useCallback(() => {
    if (!token) {
      setState('invalid');
      return;
    }

    setState('loading');
    void getAccountActivationRequest(token)
      .then((result) => {
        setActivation(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        // 400 is a link that is unknown, used, expired or no longer pending. Anything else
        // leaves the link as it was, so it is worth trying again.
        const linkRejected = error instanceof ApiError && error.status === 400;
        setState(linkRejected ? 'invalid' : 'unavailable');
      });
  }, [token]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !activation) return;

    const nextErrors: FieldErrors = {
      password: validatePassword(password),
      passwordConfirmation: passwordConfirmationError(password, passwordConfirmation),
    };
    if (activation.needsPhone && !phone.trim()) {
      nextErrors.phone = 'Ingresá un teléfono de contacto.';
    }
    setErrors(nextErrors);
    setRequestError(null);

    if (nextErrors.password || nextErrors.passwordConfirmation || nextErrors.phone) return;

    setIsSubmitting(true);
    try {
      await activateAccount({
        token,
        password,
        ...(activation.needsPhone ? { phone: phone.trim() } : {}),
      });

      // Somebody who left the quotation form to activate their account goes back to it.
      if (hasQuotationDraft()) {
        navigate('/quotation-request', { replace: true });
        return;
      }

      setState('activated');
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setRequestError(
          'Alcanzaste el límite de intentos. Esperá unos minutos y volvé a intentar.',
        );
      } else if (error instanceof ApiError && error.message === 'phone is required') {
        setErrors({ phone: 'Ingresá un teléfono de contacto.' });
      } else if (error instanceof ApiError && error.status === 400) {
        setRequestError(
          'No pudimos activar la cuenta. Revisá el teléfono y la contraseña, o pedí un enlace nuevo si este venció.',
        );
      } else {
        setRequestError('No pudimos activar tu cuenta. Volvé a intentar en unos minutos.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="TU CUENTA DE PHONETEC"
        title={state === 'activated' ? 'Tu cuenta quedó activa' : 'Activá tu cuenta'}
        description="Elegí tu contraseña para confirmar tu email y empezar a usar tu cuenta."
      />
      {state === 'loading' ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={24} color="secondary" />
          <Typography>Estamos validando tu enlace...</Typography>
        </Stack>
      ) : null}
      {state === 'unavailable' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Reintentar
            </Button>
          }
        >
          No pudimos validar tu enlace en este momento. Volvé a intentar en unos minutos.
        </Alert>
      ) : null}
      {state === 'invalid' ? (
        <Alert severity="error">
          El enlace no es válido o venció. Podés{' '}
          <Link component={RouterLink} to="/account/check-email" underline="always">
            pedir uno nuevo
          </Link>{' '}
          con tu email.
        </Alert>
      ) : null}
      {state === 'activated' ? (
        <SectionCard title="¡Listo!">
          <Stack spacing={2}>
            <Alert severity="success">Ya ingresaste con tu cuenta.</Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <PrimaryButton type="button" onClick={() => navigate('/quotation-request')}>
                Cotizar un celular
              </PrimaryButton>
              <Button type="button" color="inherit" onClick={() => navigate('/catalog')}>
                Ver el catálogo
              </Button>
            </Stack>
          </Stack>
        </SectionCard>
      ) : null}
      {state === 'ready' && activation ? (
        <SectionCard
          title={`Hola, ${activation.fullName}`}
          description={`Vas a ingresar con ${activation.email}.`}
        >
          <Stack component="form" spacing={2} onSubmit={handleSubmit} noValidate>
            {requestError ? <Alert severity="error">{requestError}</Alert> : null}
            {activation.needsPhone ? (
              <TextField
                label="Teléfono"
                type="tel"
                autoComplete="tel"
                placeholder="099 123 456"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setErrors((current) => ({ ...current, phone: undefined }));
                }}
                error={Boolean(errors.phone)}
                helperText={errors.phone || 'Lo usamos para coordinar la revisión de tus equipos.'}
                fullWidth
              />
            ) : null}
            <TextField
              label="Contraseña"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setErrors((current) => ({ ...current, password: undefined }));
              }}
              error={Boolean(errors.password)}
              helperText={
                errors.password ||
                'Mínimo 10 caracteres, con una mayúscula, un número y un carácter especial.'
              }
              fullWidth
            />
            <TextField
              label="Repetir contraseña"
              type="password"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={(event) => {
                setPasswordConfirmation(event.target.value);
                setErrors((current) => ({ ...current, passwordConfirmation: undefined }));
              }}
              error={Boolean(errors.passwordConfirmation)}
              helperText={errors.passwordConfirmation}
              fullWidth
            />
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Activando...' : 'Activar mi cuenta'}
            </PrimaryButton>
          </Stack>
        </SectionCard>
      ) : null}
    </NarrowPage>
  );
}
